import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { KeyRound, Server } from 'lucide-react'
import {
  catalogApi,
  INTEGRATION_SCOPES,
  type ApiAccessEnvironment,
  type ApiAccessOverview,
  type ApiKey,
  type ClientDetail,
  type CreateKeyInput,
  type IntegrationScope,
  type IssuedKey,
} from '@/api/catalogApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field } from '@/components/common/Field'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormError, ListState, TextAction, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { IssuedKeyDialog } from '@/components/admin/catalog/IssuedKeyDialog'
import { KeyDeliveriesDialog } from '@/components/admin/catalog/KeyDeliveriesDialog'
import { useApiAccess, useCatalogMutation } from '@/hooks/useCatalog'
import { useNow } from '@/hooks/useNow'
import { ago, canRotate, tenantConnection, webhookHealth, type Ago } from '@/lib/apiAccess'
import { cn } from '@/lib/utils'

/**
 * A client's API access, one card per product environment: its key(s) — issue,
 * rotate, disable — and the tenants that environment serves, with their CGT
 * tenant id and whether they have called yet.
 */
export function ApiAccessPanel({ client }: { client: ClientDetail }) {
  const { t } = useTranslation()
  const access = useApiAccess(client.id)
  const [issued, setIssued] = useState<{ key: IssuedKey; baseUrl: string } | null>(null)

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-ink-muted">{t('catalog.apiAccess.intro')}</p>
      <ListState query={access}>
        {access.data ? (
          access.data.environments.length === 0 ? (
            <EmptyState compact icon={Server} message={t('catalog.apiAccess.noEnvironments')} />
          ) : (
            access.data.environments.map((env) => (
              <EnvironmentCard key={env.id} env={env} overview={access.data} onIssued={(key) => setIssued({ key, baseUrl: access.data.apiBaseUrl })} />
            ))
          )
        ) : null}
      </ListState>
      {issued ? <IssuedKeyDialog issued={issued.key} baseUrl={issued.baseUrl} onClose={() => setIssued(null)} /> : null}
    </div>
  )
}

function EnvironmentCard({ env, overview, onIssued }: { env: ApiAccessEnvironment; overview: ApiAccessOverview; onIssued: (k: IssuedKey) => void }) {
  const { t } = useTranslation()
  const [creating, setCreating] = useState(false)
  const [confirm, setConfirm] = useState<{ key: ApiKey; action: 'rotate' | 'disable' } | null>(null)
  const [deliveriesOf, setDeliveriesOf] = useState<ApiKey | null>(null)
  const act = useCatalogMutation(({ key, action }: { key: ApiKey; action: 'rotate' | 'disable' | 'enable' }) =>
    action === 'rotate' ? catalogApi.rotateKey(key.id) : catalogApi.setKeyStatus(key.id, action).then(() => null),
  )
  const live = env.keys.filter((k) => k.role !== 'disabled')
  const rotating = env.keys.some((k) => k.role === 'replacement')
  const title = `${env.product.name} · ${env.name}`

  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-body font-semibold text-ink-strong">{title}</h3>
          <span className="rounded-sm bg-surface-subtle px-2 py-[3px] text-label text-ink-muted">{t(`catalog.kinds.${env.kind}`)}</span>
        </div>
        {live.length === 0 ? <Button onClick={() => setCreating(true)}>{t('catalog.apiAccess.createKey')}</Button> : null}
      </header>

      <div className="flex flex-col gap-3 px-4 py-4">
        {env.keys.length === 0 ? (
          <EmptyState compact icon={KeyRound} title={t('catalog.apiAccess.noKey')} message={t('catalog.apiAccess.noKeyHelp')} />
        ) : (
          <>
            {rotating ? <p className="rounded-md bg-note-bg px-3 py-2 text-meta text-ink">{t('catalog.apiAccess.rotatingNote')}</p> : null}
            {act.error && !confirm ? <FormError error={act.error} /> : null}
            {env.keys.map((k) => (
              <KeyRow
                key={k.id}
                apiKey={k}
                rotatable={k.role === 'active' && canRotate(env.keys)}
                onRotate={() => setConfirm({ key: k, action: 'rotate' })}
                onDisable={() => setConfirm({ key: k, action: 'disable' })}
                onEnable={() => act.mutate({ key: k, action: 'enable' })}
                onDeliveries={() => setDeliveriesOf(k)}
              />
            ))}
          </>
        )}
        <TenantTable env={env} customerId={overview.customer.id} />
      </div>

      {creating ? (
        <CreateKeyDialog
          env={env}
          customerName={overview.customer.name}
          needsContact={!overview.customer.hasContacts}
          onClose={() => setCreating(false)}
          onIssued={(k) => {
            setCreating(false)
            onIssued(k)
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirm !== null}
        destructive={confirm?.action === 'disable'}
        title={t(confirm?.action === 'disable' ? 'catalog.apiAccess.disableTitle' : 'catalog.apiAccess.rotateTitle', { name: confirm?.key.name ?? '' })}
        body={t(confirm?.action === 'disable' ? 'catalog.apiAccess.disableBody' : 'catalog.apiAccess.rotateBody')}
        confirmLabel={confirm?.action === 'disable' ? t('catalog.apiAccess.disable') : t('catalog.apiAccess.rotate')}
        pending={act.isPending}
        error={act.error ? errorMessage(act.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setConfirm(null)
            act.reset()
          }
        }}
        onConfirm={() =>
          confirm &&
          act.mutate(confirm, {
            onSuccess: (res) => {
              setConfirm(null)
              if (res) onIssued(res)
            },
          })
        }
      />
      {deliveriesOf ? <KeyDeliveriesDialog apiKey={deliveriesOf} onClose={() => setDeliveriesOf(null)} /> : null}
    </section>
  )
}

const ROLE_TONE: Record<ApiKey['role'], string> = {
  active: 'bg-status-resolved-bg text-status-resolved-fg',
  replacement: 'bg-status-progress-bg text-status-progress-fg',
  disabled: 'bg-status-closed-bg text-status-closed-fg',
}

function KeyRow({
  apiKey: k,
  rotatable,
  onRotate,
  onDisable,
  onEnable,
  onDeliveries,
}: {
  apiKey: ApiKey
  rotatable: boolean
  onRotate: () => void
  onDisable: () => void
  onEnable: () => void
  onDeliveries: () => void
}) {
  const { t } = useTranslation()
  const now = new Date(useNow())
  const agoText = useAgoText()
  const health = webhookHealth(k.webhooks)
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-3', k.role === 'disabled' && 'opacity-60')}>
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-medium text-ink-strong">{k.name}</span>
          <span className={cn('rounded-sm px-2 py-[3px] text-label font-medium', ROLE_TONE[k.role])}>{t(`catalog.apiAccess.role.${k.role}`)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-meta text-ink-muted">
          <span>
            {t('catalog.apiAccess.clientId')} <span className="font-mono text-ink">{k.clientId}</span>
          </span>
          <span>{k.scopes.map((s) => t(`catalog.apiAccess.scope.${s}`, { defaultValue: s })).join(' · ')}</span>
          <span>{k.lastUsedAt ? t('catalog.apiAccess.lastUsed', { when: agoText(ago(new Date(k.lastUsedAt), now)) }) : t('catalog.apiAccess.neverUsed')}</span>
          <span className={cn(health === 'failing' && 'font-medium text-status-escalated-fg', health === 'ok' && 'text-status-resolved-fg')}>
            {t(`catalog.apiAccess.webhook.${health}`, { count: k.webhooks.failing })}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <TextAction onClick={onDeliveries}>{t('catalog.apiAccess.deliveries')}</TextAction>
        {rotatable ? <TextAction onClick={onRotate}>{t('catalog.apiAccess.rotate')}</TextAction> : null}
        {k.role === 'disabled' ? (
          <TextAction onClick={onEnable}>{t('catalog.apiAccess.enable')}</TextAction>
        ) : (
          <TextAction danger onClick={onDisable}>
            {t('catalog.apiAccess.disable')}
          </TextAction>
        )}
      </div>
    </div>
  )
}

/** "3 min ago" from an {@link Ago}. */
function useAgoText() {
  const { t } = useTranslation()
  return (a: Ago) => (a.unit === 'now' ? t('catalog.apiAccess.ago.now') : t(`catalog.apiAccess.ago.${a.unit}`, { count: a.count }))
}

const CONNECTION_TONE = { never: 'text-ink-muted', connected: 'text-status-resolved-fg', idle: 'text-sla-warning' } as const

function TenantTable({ env, customerId }: { env: ApiAccessEnvironment; customerId: string }) {
  const { t } = useTranslation()
  const now = new Date(useNow())
  const agoText = useAgoText()
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h4 className="text-meta font-semibold uppercase tracking-wide text-ink-muted">{t('catalog.apiAccess.tenants')}</h4>
        <Link to={`/admin/clients/${customerId}?tab=tenants`} className="text-meta font-medium text-ink hover:underline">
          {t('catalog.apiAccess.editTenants')}
        </Link>
      </div>
      {env.tenants.length === 0 ? (
        <p className="text-meta text-ink-muted">{t('catalog.apiAccess.noTenants')}</p>
      ) : (
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-surface-subtle">
            <tr>
              <th className={thClass}>{t('catalog.apiAccess.tenantCols.tenant')}</th>
              <th className={thClass}>{t('catalog.apiAccess.tenantCols.externalId')}</th>
              <th className={thClass}>{t('catalog.apiAccess.tenantCols.connection')}</th>
              <th className={`${thClass} w-28`}>{t('catalog.apiAccess.tenantCols.open')}</th>
            </tr>
          </thead>
          <tbody>
            {env.tenants.map((tn) => {
              const c = tenantConnection(tn.lastSeenAt, now)
              return (
                <tr key={tn.id} className="border-b border-line">
                  <td className={`${tdClass} text-ink-strong`}>
                    {tn.name} <span className="font-mono text-label text-ink-muted">{tn.code}</span>
                  </td>
                  <td className={tdClass}>
                    {tn.externalTenantId ? (
                      <span className="break-all font-mono text-label">{tn.externalTenantId}</span>
                    ) : (
                      <span className="text-sla-warning">{t('catalog.apiAccess.notLinked')}</span>
                    )}
                  </td>
                  <td className={cn(tdClass, CONNECTION_TONE[c.state])}>{t(`catalog.apiAccess.connection.${c.state}`, { when: c.ago ? agoText(c.ago) : '' })}</td>
                  <td className={`${tdClass} font-mono`}>{tn.openTickets}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

function CreateKeyDialog({
  env,
  customerName,
  needsContact,
  onClose,
  onIssued,
}: {
  env: ApiAccessEnvironment
  customerName: string
  needsContact: boolean
  onClose: () => void
  onIssued: (k: IssuedKey) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(`${customerName} · ${env.product.name} · ${env.name}`)
  const [scopes, setScopes] = useState<IntegrationScope[]>([...INTEGRATION_SCOPES])
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const create = useCatalogMutation((input: CreateKeyInput) => catalogApi.createKey(input))
  const valid = name.trim() && scopes.length > 0 && (!needsContact || (contactName.trim() && contactEmail.includes('@')))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!valid) return
    create.mutate(
      {
        customerEnvironmentId: env.id,
        name: name.trim(),
        scopes,
        ...(needsContact ? { defaultContact: { name: contactName.trim(), email: contactEmail.trim() } } : {}),
      },
      { onSuccess: onIssued },
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t('catalog.apiAccess.createTitle', { env: `${env.product.name} · ${env.name}` })}</DialogTitle>
          </DialogHeader>
          <Field id="key-name" label={t('catalog.apiAccess.keyName')}>
            <Input id="key-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
          </Field>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-meta font-medium text-ink">{t('catalog.apiAccess.scopes')}</legend>
            {INTEGRATION_SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-body text-ink">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={(e) => setScopes((cur) => (e.target.checked ? [...cur, s] : cur.filter((x) => x !== s)))}
                />
                {t(`catalog.apiAccess.scope.${s}`)} <span className="font-mono text-label text-ink-muted">{s}</span>
              </label>
            ))}
          </fieldset>
          {needsContact ? (
            <div className="flex flex-col gap-3 rounded-md border border-line px-3 py-3">
              <div>
                <p className="text-meta font-medium text-ink">{t('catalog.apiAccess.defaultContact')}</p>
                <p className="text-meta text-ink-muted">{t('catalog.apiAccess.defaultContactHelp')}</p>
              </div>
              <Field id="contact-name" label={t('catalog.apiAccess.contactName')}>
                <Input id="contact-name" value={contactName} maxLength={200} onChange={(e) => setContactName(e.target.value)} />
              </Field>
              <Field id="contact-email" label={t('catalog.apiAccess.contactEmail')}>
                <Input id="contact-email" type="email" value={contactEmail} maxLength={254} onChange={(e) => setContactEmail(e.target.value)} />
              </Field>
            </div>
          ) : null}
          {create.error ? <FormError error={create.error} /> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {t('catalog.apiAccess.createKey')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
